// src/config/telas.js — REGISTRO ÚNICO DE TELAS
//
// Fonte da verdade da identificação de cada tela do ERP: módulo, título e código
// (OBR-001, RH-001…). Quem consome:
//
//   · components/shellShadcn.js  → monta o breadcrumb do topo
//   · components/sub-bar.js      → publica window._subBarCtx (usado pelo helpdesk
//                                  em "Onde aconteceu" ao abrir um chamado)
//
// POR QUE UM REGISTRO ESTÁTICO, e não o `window._subBarCtx` que cada módulo setava:
//   `updateActive()` roda no evento `hashchange` (main.js), ANTES de o módulo da
//   nova rota renderizar (router.js: `await mod.render(params)`). Um breadcrumb que
//   lesse o global veria o contexto da tela ANTERIOR. O registro é resolvido pela
//   rota, então não há corrida.
//
// O `modulo` NÃO é declarado aqui: ele é derivado do grupo da sidebar. Assim é
// impossível a tela dizer que é de "COMERCIAL" enquanto mora em Operações no menu —
// divergência que existia em 7 telas.

import { NAV_GROUPS_ADMIN } from '../components/sidebar.js';

// ── Código por rota. Só o código: título e módulo vêm da sidebar. ───────────────
// Duplicatas resolvidas nesta passada (o código é a identidade da tela no suporte):
//   · orcamentos           COM-001 → FIN-009  (colidia com clientes; e mora em Financeiro)
//   · portfolio            COM-002 → COM-003  (colidia com contratos)
//   · funcionario_perfil   RH-007  → RH-012   (colidia com funcionários)
const CODIGOS = {
  // Inteligência
  dashboard: 'POR-001',
  bi: 'BI-001',
  rh: 'RH-001',
  mapa: 'OBR-006',
  assistente: 'INT-005',
  // Operações
  obras: 'OBR-001',
  clientes: 'COM-001',
  etapas: 'ETAPAS',
  tarefas: 'PRJ-001',
  cronograma: 'PRJ-002',
  obras_entregues: 'OBR-003',
  garantias: 'GAR',
  portfolio: 'COM-003',
  // Financeiro
  contas_receber: 'FIN-001',
  contas_pagar: 'FIN-002',
  dre: 'FIN-003',
  inadimplentes: 'FIN-007',
  recorrencias: 'FIN-008',
  financeiro: 'FIN-006',
  despesas: 'FIN-DESPESAS',
  reembolsos: 'FIN-REEMB',
  orcamentos: 'FIN-009',
  compras: 'FIN-COMPRAS',
  nfse_tomadas: 'FIN-NFSE',
  notas: 'FIN-NF',
  // Equipe
  funcionarios: 'RH-007',
  terceirizados: 'TERC-001',
  ponto: 'RH-PONTO',
  espelho_ponto: 'RH-010',
  horas: 'RH-HORAS',
  docs_funcionarios: 'RH-011',
  ausencias: 'RH-006',
  ferias_rh: 'RH-008',
  // Comercial
  contratos: 'COM-002',
  fornecedores: 'FORN-001',
  arquivos: 'POR-003',
  // Atividades
  relatorios: 'BI-002',
  atividades: 'ATV-001',
  meus_chamados: 'SUP-HLP',
  // Sistema
  configuracoes: 'ADM-003',
  usuarios: 'ADM-001',
  upgrade: 'ADM-006',
  sobre: 'ADM-004',
};

// ── Telas que NÃO estão na sidebar. ────────────────────────────────────────────
// Era exatamente a ausência daqui que fazia o breadcrumb cair no fallback e
// imprimir o nome cru da rota ("obra", em minúsculo).
//   pai · rota da listagem de onde a tela nasce — vira o nível clicável do breadcrumb.
const FORA_DO_NAV = {
  obra:               { modulo: 'Operações', pagina: 'Detalhe da Obra', id: 'OBR-007', pai: 'obras' },
  funcionario_perfil: { modulo: 'Equipe',    pagina: 'Perfil 360°',     id: 'RH-012',  pai: 'funcionarios' },
  wirekit:            { modulo: 'Sistema',   pagina: 'Wirekit · Componentes', id: 'WK-000' },
};

let _indice = null;

function _montarIndice() {
  const ix = {};
  for (const g of NAV_GROUPS_ADMIN) {
    for (const it of g.items) {
      ix[it.page] = {
        modulo: g.label,        // ← derivado da sidebar: não há como divergir
        pagina: it.label,
        id: CODIGOS[it.page] || null,
        pai: null,
      };
    }
  }
  for (const [page, t] of Object.entries(FORA_DO_NAV)) ix[page] = { pai: null, ...t };
  return ix;
}

/** Identificação canônica de uma rota. `null` se a rota não estiver registrada. */
export function getTela(page) {
  if (!_indice) _indice = _montarIndice();
  return _indice[page] || null;
}

/** Todas as telas registradas — usado pelo teste de integridade. */
export function todasAsTelas() {
  if (!_indice) _indice = _montarIndice();
  return { ..._indice };
}
